import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
} from 'services/devices/devices.api';
import { buildDevicesBySystemAndTypeMap, buildDeviceValueMap } from '../../../utils/builder.utils';
import { globalActionDeviceDataMaps } from '../../../utils/deviceFieldMetadata.utils';
import DeviceCustomInput from '../DeviceCustomInput';
import DeviceLatestStatus from '../DeviceLatestStatus';
import {
  buildDeviceCompareValueSelectOptions,
  buildDeviceOperationSelectOptions,
  buildDeviceSelectOptionsForDeviceType,
  buildDeviceSubTypeSelectOptions,
  buildDeviceTypeSelectOptions,
  buildDeviceValueSelectOptions,
  buildSystemSelectOptions,
  hasDeviceSubTypeOptions,
  isSelectableDeviceType,
  operators,
} from './utils/deviceCondition.utils';

const DeviceCondition = ({ setData, data, setError, readOnly = false }) => {
  const { data: connectedDevices = [], isLoading: loadingConnectedDevices } =
    useGetConnectedDevicesQuery();
  const { data: deviceTypes = {} } = useGetAllAvailableDeviceTypesQuery();
  const devicesBySystemAndTypeMap =
    connectedDevices.length > 0 ? buildDevicesBySystemAndTypeMap(connectedDevices) : new Map();
  const [systemType, setSystemType] = useState();
  const [deviceType, setDeviceType] = useState();
  const [deviceSubType, setDeviceSubType] = useState();

  const updateData = (item) => {
    if (item.selectedDevice && item.selectedDevice.data) {
      item.selectedDevice.data = [item.selectedDevice.data[0]];
    }
    setData({
      ...data,
      ...item,
      error: verifyData({ ...data, ...item }),
    });
  };

  const verifyData = (obj) => {
    let error = false;
    if (
      obj.node_id === '' ||
      obj.deviceType === '' ||
      obj.keyToCheck === '' ||
      (obj.keyToCompare === '' && obj.valueToCompare === '') ||
      (obj.keyToCompare === null && obj.valueToCompare === null) ||
      obj.operator === '' ||
      obj.operator === null
    ) {
      error = true;
      setError({ message: 'Action incomplete, check for missing fields.' });
    }
    if (!error) setError(null);
    return error;
  };

  const systemLocked =
    readOnly && !!data.deviceType && !!systemType && !devicesBySystemAndTypeMap?.has(systemType);
  const deviceTypeLocked =
    readOnly &&
    !!data.deviceType &&
    !!deviceType &&
    !devicesBySystemAndTypeMap?.get(systemType)?.has(deviceType);
  const subTypeLocked =
    readOnly && !!deviceSubType && !devicesBySystemAndTypeMap?.get(systemType)?.has(deviceType);
  const aliasLocked =
    readOnly &&
    !!data.node_id &&
    !connectedDevices.some((d) => d.node_id?.toString() === data.node_id?.toString());
  const systemOptions = buildSystemSelectOptions(deviceTypes, devicesBySystemAndTypeMap);
  const deviceTypeOptions = buildDeviceTypeSelectOptions(
    deviceTypes,
    devicesBySystemAndTypeMap,
    systemType,
  );
  const valueMap = buildDeviceValueMap(data.selectedDevice, globalActionDeviceDataMaps);
  const subTypeOptions = buildDeviceSubTypeSelectOptions(deviceTypes, systemType, deviceType);
  const deviceOptions = buildDeviceSelectOptionsForDeviceType(connectedDevices, data.deviceType);
  const deviceValueOptions = buildDeviceValueSelectOptions(valueMap);
  const deviceOperationOptions = buildDeviceOperationSelectOptions(
    operators,
    valueMap[data.keyToCheck]?.type,
  );
  const deviceCompareValueOptions = buildDeviceCompareValueSelectOptions(
    valueMap,
    valueMap[data.keyToCheck]?.units,
  );

  useEffect(() => {
    if (data.deviceType) {
      if (data.deviceType.includes('RescAlert Devices')) {
        setSystemType('RescAlert Devices');
        const types = data.deviceType.replace('RescAlert Devices ', '');
        setDeviceType(types);
        setDeviceSubType(types[1]);
      } else {
        const firstSpace = data.deviceType.indexOf(' ');
        const nextSystemType = data.deviceType.slice(0, firstSpace);
        const rest = data.deviceType.slice(firstSpace + 1);
        const nextDeviceType = rest.split('_')[0];
        const subType = rest.includes('_') ? rest.split('_')[1] : undefined;
        setSystemType(nextSystemType);
        setDeviceType(nextDeviceType);
        setDeviceSubType(subType);
      }
    }
  }, [data.deviceType]);

  return (
    <>
      {!loadingConnectedDevices ? (
        <div className="d-flex flex-column w-100">
          <div className="global-action-builder-row d-flex flex-column flex-md-row p-3 rounded-2 m-2 gap-3 align-items-stretch align-items-md-center">
            <BaseSelect
              label="System:"
              wrapperClassName="flex-fill"
              isSearchable
              isClearable
              classNames={{
                control: () => 'global-action-builder-field',
              }}
              selectProps={{
                options: systemOptions,
                value: findSelectOption(systemOptions, systemType),
                placeholder: 'Select',
                isDisabled: readOnly || systemLocked,
                onChange: (option) => {
                  setSystemType(option?.value ?? '');
                  setDeviceType('');
                  setDeviceSubType('');
                  updateData({
                    deviceType: '',
                    node_id: null,
                    selectedDevice: null,
                    keyToCheck: null,
                    valueToCompare: null,
                    keyToCompare: null,
                    operator: null,
                  });
                },
              }}
            />

            {systemType && (
              <BaseSelect
                label="Device Type:"
                wrapperClassName="flex-fill"
                isSearchable
                isClearable
                classNames={{
                  control: () => 'global-action-builder-field',
                }}
                selectProps={{
                  options: deviceTypeOptions,
                  value: findSelectOption(deviceTypeOptions, deviceType),
                  placeholder: 'Select',
                  isDisabled: readOnly || deviceTypeLocked,
                  onChange: (option) => {
                    const nextDeviceType = option?.value ?? '';

                    setDeviceType(nextDeviceType);
                    setDeviceSubType('');

                    if (isSelectableDeviceType(deviceTypes, systemType, nextDeviceType)) {
                      updateData({
                        deviceType: systemType + ' ' + nextDeviceType,
                        node_id: null,
                        selectedDevice: null,
                        keyToCheck: null,
                        valueToCompare: null,
                        keyToCompare: null,
                        operator: null,
                      });
                    } else {
                      updateData({
                        deviceType: '',
                        node_id: null,
                        selectedDevice: null,
                        keyToCheck: null,
                        valueToCompare: null,
                        keyToCompare: null,
                        operator: null,
                      });
                    }
                  },
                }}
              />
            )}

            {deviceType && hasDeviceSubTypeOptions(deviceTypes, systemType, deviceType) && (
              <BaseSelect
                label="Sub Type:"
                wrapperClassName="flex-fill"
                isSearchable
                isClearable
                classNames={{
                  control: () => 'global-action-builder-field',
                }}
                selectProps={{
                  options: subTypeOptions,
                  value: findSelectOption(subTypeOptions, deviceSubType),
                  placeholder: 'Select',
                  isDisabled: readOnly || subTypeLocked,
                  onChange: (option) => {
                    const nextDeviceSubType = option?.value ?? '';
                    setDeviceSubType(nextDeviceSubType);
                    updateData({
                      deviceType: nextDeviceSubType
                        ? systemType + ' ' + deviceType + '_' + nextDeviceSubType
                        : '',
                      node_id: null,
                      selectedDevice: null,
                      keyToCheck: null,
                      valueToCompare: null,
                      keyToCompare: null,
                      operator: null,
                    });
                  },
                }}
              />
            )}

            {data.deviceType && (
              <BaseSelect
                label="Device Alias:"
                wrapperClassName="flex-fill"
                isSearchable
                isClearable
                classNames={{
                  control: () => 'global-action-builder-field',
                }}
                selectProps={{
                  options: deviceOptions,
                  value: findSelectOption(deviceOptions, data.node_id),
                  placeholder: 'Select',
                  isDisabled: readOnly || aliasLocked,
                  onChange: (option) => {
                    const device = option?.device;
                    updateData({
                      node_id: option?.value ?? '',
                      selectedDevice: device ? { ...device, data: [device.data[0]] } : null,
                      keyToCheck: '',
                      valueToCompare: '',
                      keyToCompare: '',
                      operator: '',
                    });
                  },
                }}
              />
            )}
          </div>
          {data.selectedDevice && (
            <DeviceLatestStatus
              device={data.selectedDevice}
              dataMaps={globalActionDeviceDataMaps}
            />
          )}
          {data.selectedDevice && (
            <div className="global-action-builder-row d-flex p-3 rounded-2 m-2 mt-0 w-auto h-auto flex-column flex-md-row gap-3 align-items-stretch align-items-md-center">
              <BaseSelect
                label="Value:"
                wrapperClassName="flex-fill"
                isSearchable
                isClearable
                classNames={{
                  control: () => 'global-action-builder-field',
                }}
                selectProps={{
                  options: deviceValueOptions,
                  value: findSelectOption(deviceValueOptions, data.keyToCheck),
                  placeholder: 'Select',
                  isDisabled: readOnly,
                  onChange: (option) => {
                    const value = option?.value ?? '';
                    if (!value) {
                      updateData({ keyToCheck: value, operator: null, keyToCompare: '' });
                    } else {
                      updateData({ keyToCheck: value });
                    }
                  },
                }}
              />
              {data.keyToCheck && (
                <BaseSelect
                  label="Operator:"
                  wrapperClassName="flex-fill"
                  classNames={{
                    control: () => 'global-action-builder-field',
                  }}
                  isSearchable
                  isClearable
                  selectProps={{
                    options: deviceOperationOptions,
                    value: findSelectOption(deviceOperationOptions, data.operator),
                    placeholder: 'Select',
                    isDisabled: readOnly,
                    onChange: (option) => {
                      const operator = option?.value || '';
                      updateData({ operator: operator });
                    },
                  }}
                />
              )}
              {data.operator &&
                (data.inputValueType ? (
                  <div className="flex-fill d-flex flex-column gap-1 fw-medium">
                    <div>Custom Value:</div>
                    <DeviceCustomInput
                      value={data.valueToCompare}
                      onChange={(value) => updateData({ valueToCompare: value })}
                      dataMapItem={valueMap[data.keyToCheck]}
                      readOnly={readOnly}
                    />
                  </div>
                ) : (
                  <BaseSelect
                    label="Compare Value:"
                    wrapperClassName="flex-fill"
                    classNames={{
                      control: () => 'global-action-builder-field',
                    }}
                    isSearchable
                    isClearable
                    selectProps={{
                      options: deviceCompareValueOptions,
                      value: findSelectOption(deviceCompareValueOptions, data.keyToCompare),
                      placeholder: 'Select',
                      isDisabled: readOnly,
                      onChange: (option) => {
                        const keyToCompare = option?.value || '';
                        updateData({ keyToCompare: keyToCompare });
                      },
                    }}
                  />
                ))}
              {data.operator && (
                <div className="d-flex flex-column gap-1">
                  {/* div is just used to line up the button with the other inputs (aria-hidden hides it from screen readers) */}
                  <div className="fw-medium invisible" aria-hidden="true">
                    Action
                  </div>
                  <Button
                    variant="light"
                    size="sm"
                    className="rounded-2 text-nowrap w-auto"
                    active={!!data.inputValueType}
                    onClick={() =>
                      updateData({
                        valueToCompare: '',
                        keyToCompare: '',
                        inputValueType: !data.inputValueType,
                      })
                    }
                  >
                    {data.inputValueType ? 'Change to compare' : 'Change to custom value'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <WidgetLoadingAnimation />
      )}
    </>
  );
};

export default DeviceCondition;
